import { useCallback } from 'react';
import { INITIAL_CONTEXT_MENU_STATE } from '../constants';
import { insertTagAtMention, removeMentionTrigger, findMentionTrigger } from '../tiptapEditorUtils';
import { getTagDisplay } from '../utils';
import type { Editor } from '@tiptap/react';
import type { PromptEditorContextMenuState, AvailableInput } from '../types';
import type { PromptDisplayMode } from 'web';

interface UseMentionMenuKeyboardParams {
  editor: Editor | null;
  contextMenu: PromptEditorContextMenuState;
  handleSetContextMenu: (newState: PromptEditorContextMenuState) => void;
  displayMode: PromptDisplayMode;
  onCreatePrompt: (queryText: string) => AvailableInput | undefined;
}

export const useMentionMenuKeyboard = ({
  editor,
  contextMenu,
  handleSetContextMenu,
  displayMode,
  onCreatePrompt,
}: UseMentionMenuKeyboardParams) => {
  /**
   * Update the queryText attribute of the mention trigger tag at the specified position.
   */
  const updateMentionQueryText = useCallback(
    (triggerPos: number, newQueryText: string): void => {
      if (!editor) return;

      const { tr } = editor.state;
      const node = editor.state.doc.nodeAt(triggerPos);
      if (node) {
        tr.setNodeMarkup(triggerPos, undefined, {
          ...node.attrs,
          queryText: newQueryText,
        });
        editor.view.dispatch(tr);
      }
    },
    [editor],
  );

  /**
   * Shared keyboard handler for mention menu editing.
   * Handles Enter, Escape, Backspace, and printable characters when the mention menu is open.
   * This function is called from both handleDOMEvents (editor focus) and global listener (menu focus).
   */
  const handleMentionMenuKeyboard = useCallback(
    (event: KeyboardEvent): boolean => {
      if (!editor) return false;
      if (contextMenu.type !== 'mention') return false;

      // Handle Enter to create a new prompt from queryText
      if (event.key === 'Enter') {
        const { triggerPos, queryText } = findMentionTrigger(editor);
        if (triggerPos < 0) return false;
        event.preventDefault();
        event.stopPropagation();

        const trimmedQueryText = queryText.trim();
        if (trimmedQueryText) {
          const inputHandle = onCreatePrompt(trimmedQueryText);
          handleSetContextMenu(INITIAL_CONTEXT_MENU_STATE);

          if (inputHandle) {
            insertTagAtMention({
              editor,
              tagKey: inputHandle.key,
              ...getTagDisplay({ ...inputHandle, value: trimmedQueryText }, displayMode),
            });
          } else {
            removeMentionTrigger(editor, true);
          }
        }
        return true;
      }

      // Handle Escape to close menu and convert mention tag to plain text
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();

        removeMentionTrigger(editor, true);
        handleSetContextMenu(INITIAL_CONTEXT_MENU_STATE);

        // Defer focus to after React re-render completes
        requestAnimationFrame(() => {
          editor.view.focus();
        });

        return true;
      }

      // Handle Backspace to remove last char from queryText or close menu
      if (event.key === 'Backspace') {
        event.preventDefault();
        event.stopPropagation();
        const { triggerPos, queryText } = findMentionTrigger(editor);

        if (triggerPos < 0) return false;

        if (queryText.length > 0) {
          // Remove last character from the mention tag's queryText
          const newQueryText = queryText.slice(0, -1);
          updateMentionQueryText(triggerPos, newQueryText);
          handleSetContextMenu({ ...contextMenu, queryText: newQueryText });
        } else {
          // No queryText left, remove the trigger tag and close menu
          removeMentionTrigger(editor, false);
          handleSetContextMenu(INITIAL_CONTEXT_MENU_STATE);

          // Defer focus to after React re-render completes
          requestAnimationFrame(() => {
            editor.view.focus();
          });
        }
        return true;
      }

      // Handle printable characters: append to the mention tag's queryText
      if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        event.stopPropagation();
        const { triggerPos, queryText } = findMentionTrigger(editor);

        if (triggerPos >= 0) {
          const newQueryText = queryText + event.key;
          updateMentionQueryText(triggerPos, newQueryText);
          handleSetContextMenu({ ...contextMenu, queryText: newQueryText });
        }
        return true;
      }

      return false;
    },
    [editor, contextMenu, handleSetContextMenu, displayMode, onCreatePrompt, updateMentionQueryText],
  );

  return {
    handleMentionMenuKeyboard,
  };
};
